import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { isEmbeddedBrowser, openInSystemBrowser } from '../utils/browser';

import '../static/styles/EmbeddedBrowserNotice.css';

const STORAGE_KEY = 'embedded-browser-notice-dismissed';

const defaultMessages = {
    title: 'Open in your browser',
    description: 'Google sign-in is blocked inside this in-app browser. Open the site in your default browser to continue.',
    hint: 'If nothing happens, use the share or options menu and choose “Open in browser”. We also copied the link so you can paste it manually.',
    primaryCta: 'Open in browser',
    secondaryCta: 'Continue here',
};

export default function EmbeddedBrowserNotice({ messages = defaultMessages }) {
    const [visible, setVisible] = useState(false);
    const [showHint, setShowHint] = useState(false);
    const [customMessages, setCustomMessages] = useState(null);
    const [persistOnClose, setPersistOnClose] = useState(true);

    const visibilityTimerRef = useRef(null);
    const visibilityHandlerRef = useRef(null);

    const resolvedMessages = useMemo(
        () => ({ ...defaultMessages, ...messages, ...(customMessages || {}) }),
        [messages, customMessages],
    );

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        if (isEmbeddedBrowser()) {
            return undefined;
        }

        try {
            const url = new URL(window.location.href);
            if (url.searchParams.has('openExternalBrowser')) {
                url.searchParams.delete('openExternalBrowser');
                const nextUrl = `${url.pathname}${url.search}${url.hash}`;
                window.history.replaceState(null, '', nextUrl);
            }
        } catch (_error) {
            // ignore clean-up issues
        }

        return undefined;
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const handleShow = (event) => {
            const detail = event?.detail || {};
            const nextMessages = detail.messages || null;
            const shouldPersist = detail.persist;
            const shouldShowHint = detail.showHint;

            if (!isEmbeddedBrowser()) {
                return;
            }

            setCustomMessages(nextMessages);
            setPersistOnClose(
                typeof shouldPersist === 'boolean'
                    ? shouldPersist
                    : !detail.force,
            );
            setShowHint(typeof shouldShowHint === 'boolean' ? shouldShowHint : false);
            setVisible(true);
        };

        window.addEventListener('embedded-browser:show', handleShow);
        return () => {
            window.removeEventListener('embedded-browser:show', handleShow);
        };
    }, []);

    const clearVisibilityWatchers = useCallback(() => {
        if (visibilityHandlerRef.current && typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', visibilityHandlerRef.current);
            visibilityHandlerRef.current = null;
        }

        if (visibilityTimerRef.current) {
            clearTimeout(visibilityTimerRef.current);
            visibilityTimerRef.current = null;
        }
    }, []);

    useEffect(() => () => {
        clearVisibilityWatchers();
    }, [clearVisibilityWatchers]);

    const resetOverlayState = useCallback(() => {
        setCustomMessages(null);
        setShowHint(false);
        setPersistOnClose(true);
    }, []);

    const persistDismissal = useCallback(() => {
        if (!persistOnClose) {
            return;
        }

        try {
            window.sessionStorage.setItem(STORAGE_KEY, '1');
        } catch (_error) {
            // ignore storage issues
        }
    }, [persistOnClose]);

    const handleOpen = useCallback(() => {
        if (typeof window === 'undefined') {
            setShowHint(true);
            return;
        }

        if (typeof document === 'undefined') {
            setShowHint(true);
            return;
        }

        setShowHint(false);

        clearVisibilityWatchers();

        let completed = false;

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                completed = true;
                clearVisibilityWatchers();
                persistDismissal();
                resetOverlayState();
                setVisible(false);
            }
        };

        visibilityHandlerRef.current = handleVisibilityChange;
        document.addEventListener('visibilitychange', handleVisibilityChange);

        visibilityTimerRef.current = window.setTimeout(() => {
            if (!completed) {
                clearVisibilityWatchers();
                setShowHint(true);
            }
        }, 1600);

        const opened = openInSystemBrowser(window.location.href, { forceExternalParam: true });

        if (!opened) {
            clearVisibilityWatchers();
            setShowHint(true);
        }
    }, [clearVisibilityWatchers, persistDismissal, resetOverlayState]);

    const handleDismiss = useCallback(() => {
        clearVisibilityWatchers();
        persistDismissal();
        resetOverlayState();
        setVisible(false);
    }, [clearVisibilityWatchers, persistDismissal, resetOverlayState]);

    if (!visible) {
        return null;
    }

    return (
        <div className="embedded-browser-notice">
            <div className="embedded-browser-notice__card">
                <h2 className="embedded-browser-notice__title">{resolvedMessages.title}</h2>
                <p className="embedded-browser-notice__description">{resolvedMessages.description}</p>
                <div className="embedded-browser-notice__actions">
                    <button type="button" className="embedded-browser-notice__primary" onClick={handleOpen}>
                        {resolvedMessages.primaryCta}
                    </button>
                    <button type="button" className="embedded-browser-notice__secondary" onClick={handleDismiss}>
                        {resolvedMessages.secondaryCta}
                    </button>
                </div>
                {showHint && (
                    <p className="embedded-browser-notice__hint">
                        {resolvedMessages.hint}
                    </p>
                )}
            </div>
        </div>
    );
}
